import React, { memo, useContext, useMemo } from 'react';
import { default as Form } from 'components/connectedForm';
import * as input from 'components/connectedForm/inputComponents';
import {
  GenericArrayCard,
  GenericArrayCardSubtitle,
  GenericArrayCardTitle,
} from '../../components/connectedForm/GenericArray';
import * as entities from '../../graphql/entities';
import getIn from '../../components/connectedForm/utils/getIn';
import Label from '../../components/connectedForm/Label';
import HeaderStatus from '../../components/connectedForm/HeaderStatus';
import { InfoButton } from '../../components/connectedForm/Button';
import { isEqual } from 'lodash';
import { useCompany, useProductConfig } from '../../utils/FetchConfig';
import { useDispatch } from 'react-redux';
import CostField from '../../components/CostField';
import CurrencyContext from '../../components/Context/CurrencyContext';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import { serviceActions } from '../../state/modules/service';
import { useBaseCurrencyAlpha3 } from '../../utils/CurrencyUtil';

const SupplierRow = ({ value: rowData }) => {
  const baseCurrencyAlpha3 = useBaseCurrencyAlpha3();

  let email, name, status;
  if (!rowData?.supplier) {
    name = 'New Supplier';
    email = null;
  } else {
    name = rowData.supplier.name;
    email = rowData.supplier.email;
  }

  if (rowData?.mainSupplier) {
    status = <HeaderStatus statusStyle={'info'} title={'Main'} />;
  } else {
    status = <HeaderStatus title={`Alternate`} statusStyle={'basic'} />;
  }

  let currency = null;

  if (
    rowData?.supplier?.currency?.isoCode?.codeAlpha3 &&
    rowData.supplier.currency.isoCode.codeAlpha3 !== baseCurrencyAlpha3
  ) {
    currency = (
      <HeaderStatus icon="account_balance" title={rowData.supplier.currency.isoCode.codeAlpha3} statusStyle={'basic'} />
    );
  }

  return (
    <GenericArrayCard>
      <GenericArrayCardTitle>{name}</GenericArrayCardTitle>
      <Form.Fieldset cols={1 / 2}>
        {status}
        {currency}
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 2}>
        <GenericArrayCardSubtitle>{email}</GenericArrayCardSubtitle>
      </Form.Fieldset>
    </GenericArrayCard>
  );
};

const SupplierPriceField = ({ priceIndex, prefix, supplierDetails }) => {
  const company = useCompany();
  const productConfig = useProductConfig();
  const title = productConfig?.[`costPriceTitle${priceIndex}`];
  if (!title) return null;

  let quantityBreakField = null;

  const priceMatrix = supplierDetails && supplierDetails.activeSupplierPriceMatrix;
  const priceMatrixCalculation = getIn(priceMatrix, `price${priceIndex}Calculation`);
  const readOnlyMessage = `Calculated from ${getIn(
    priceMatrix,
    `price${priceIndex}Calculation.priceBase`
  )} via price matrix for '${getIn(priceMatrix, 'name')}'`;

  if (priceIndex > 1) {
    quantityBreakField = (
      <Form.Field
        dataid={`${prefix}.costPrices.costPriceBreak${priceIndex}`}
        component={input.BlurTextField}
        label="for"
        readOnly={priceMatrixCalculation}
      />
    );
  }

  return (
    <Form.Row>
      <Form.Fieldset cols={2 / 6}>
        <Label>{title}</Label>
      </Form.Fieldset>
      <Form.Fieldset cols={2 / 6}>
        <Form.Field
          dataid={`${prefix}.costPrices.costPrice${priceIndex}`}
          component={input.BlurTextField}
          temporary={true}
          readOnly={priceMatrixCalculation}
          readOnlyMessage={readOnlyMessage}
          includeLabel={false}
          type={'currency'}
          decimals={company?.costPricePrecision ?? 2}
        />
      </Form.Fieldset>
      <Form.Fieldset cols={2 / 6}>{quantityBreakField}</Form.Fieldset>
    </Form.Row>
  );
};

const SupplierDetails = ({ value: rowData, rowIndex, updateMainSupplier }) => {
  const prefix = `alternateSupplierDetails.${rowIndex}`;

  const company = useCompany();
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const productConfig = useProductConfig();

  const defaultPrecision = productConfig?.defaultPrecision ?? 0;

  const addUuidConnectedFormContext = useMemo(() => {
    const addUuidChange = (field, value) => {
      const productSupplierDetails = connectedForm.values.alternateSupplierDetails?.[rowIndex];
      if (!productSupplierDetails?.uuid) {
        //Initialize UUID on the productSupplierDetails row before any other changes.
        dispatch(connectedForm.change(`${prefix}.uuid`, uuIdGenerator()));
      }
      return connectedForm.change(field, value);
    };

    return { ...connectedForm, change: addUuidChange };
  }, [connectedForm, dispatch, prefix, rowIndex]);

  return (
    <Form.Row>
      <ConnectedFormContext.Provider value={addUuidConnectedFormContext}>
        <CurrencyContext.Provider
          value={{ currency: rowData.costsInCurrency ? rowData?.supplier?.currency : company?.baseCurrency }}
        >
          <Form.Fieldset cols={1 / 2}>
            <Form.Legend label={'Supplier Details'} />
            <Form.Field
              dataid={`${prefix}.supplier`}
              component={input.Dropdown}
              label={'Supplier'}
              {...entities.Supplier.dropdown}
              initialized={true}
              temporary={true}
            />
            <Form.Field dataid={`${prefix}.partNumber`} component={input.BlurTextField} label={'Part Number'} />
            <Form.Field dataid={`${prefix}.barcode`} component={input.BlurTextField} label={'Barcode'} />
            <Form.Field
              dataid={`${prefix}.orderQuantityMinimum`}
              component={input.BlurTextField}
              label={'Minimum Quantity'}
            />
            <Form.Field
              dataid={`${prefix}.orderQuantityIncrement`}
              component={input.BlurTextField}
              label={'Increment'}
            />
            <Form.Field dataid={`${prefix}.leadTimeDays`} component={input.BlurTextField} label={'Lead'} />
            <Form.Field
              dataid={`${prefix}.allowMatrix`}
              component={input.Checkbox}
              label="Allow Supplier Matrix"
              temporary={true}
            />
            <Form.Field
              dataid={`${prefix}.costsInCurrency`}
              component={input.Checkbox}
              label="Costs in Supplier Currency"
            />
            <Form.Field dataid={`${prefix}.deleted`} component={input.Checkbox} label="Deleted" />
            <Form.Field
              dataid={`${prefix}.stockLevel`}
              component={input.BlurTextField}
              label="Stock Level"
              decimals={defaultPrecision}
            />

            {getIn(rowData, `mainSupplier`) ? null : (
              <InfoButton small={true} onClick={() => updateMainSupplier(rowIndex)}>
                Set as Main Supplier
              </InfoButton>
            )}
          </Form.Fieldset>
          <CostField>
            <Form.Fieldset cols={1 / 2}>
              <Form.Legend label={'Cost Prices'} />
              {rowData?.supplier && (
                <>
                  <SupplierPriceField priceIndex={1} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={2} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={3} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={4} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={5} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={6} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={7} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={8} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={9} prefix={prefix} supplierDetails={rowData} />
                  <SupplierPriceField priceIndex={10} prefix={prefix} supplierDetails={rowData} />
                </>
              )}
            </Form.Fieldset>
          </CostField>
        </CurrencyContext.Provider>
      </ConnectedFormContext.Provider>
    </Form.Row>
  );
};

const ProductSupplierConfig = (props) => {
  const dispatch = useDispatch();

  const suppliers = props.values?.alternateSupplierDetails || [];

  /*Dispatch a change to update the primary contact for the Customer/Supplier.*/
  const updateMainSupplier = (rowIndex) => {
    const supplierAtIndex = suppliers[rowIndex];

    if (supplierAtIndex.supplier.archived) {
      dispatch(
        serviceActions.genericAlert('Invalid action', 'Supplier is archived and cannot be set as the main supplier.')
      );
      return;
    }

    const newSuppliers = suppliers.map((supplier, index) => {
      const newSupplier = { ...supplier };
      newSupplier.mainSupplier = index === rowIndex;
      return newSupplier;
    });

    dispatch(props.change(`alternateSupplierDetails`, newSuppliers));
  };

  return (
    <input.GenericArray
      value={suppliers}
      component={input.GenericArray}
      label="Supplier Details"
      renderRow={SupplierRow}
      renderDetails={SupplierDetails}
      enableNewRows={true}
      forceUpdate={props.forceUpdate}
      renderRowIsComponent={true}
      detailProps={{ updateMainSupplier }}
    />
  );
};

export default memo(ProductSupplierConfig, (prev, next) =>
  isEqual(prev.values?.alternateSupplierDetails, next.values?.alternateSupplierDetails)
);
